
import { UltravoxSession } from 'ultravox-client';
import { Room, RoomEvent } from 'livekit-client';
import { PipecatClient } from '@pipecat-ai/client-js';
import { SmallWebRTCTransport } from '@pipecat-ai/small-webrtc-transport';
import { api } from '../lib/client';

const backend = new URL(process.env.REACT_APP_BACKEND_SERVER);

// `pipecatClient` holds the active PipecatClient for stopAgent() teardown,
// peer-to-peer WebRTC to the worker via SmallWebRTCTransport.
let session, room, pipecatClient, listenerId, socket, ws;

/**
 * Create a persistent agent definition (no session/listener started).
 * Used by Save and by createAgent when no agentId is provided.
 */
export async function createAgentDefinition({ name, description, modelName, prompt, options, functions, mcpServers, keys }) {
  const body = { modelName, prompt, options, functions, mcpServers, keys };
  if (name != null) body.name = name;
  if (description != null) body.description = description;
  const { data } = await api.post('/agents', body);
  return data;
}

/**
 * Start a session (listener) for an existing agent. Does not create or delete the agent definition.
 */
export async function startAgentSession(agentId, { options, functions, onClose, onMessage, audioModel, mediaType, phoneNumber, outboundNumber, endpointId }) {
  let instance;
  try {
    // Inbound on SIP registration: `id` (phone-registration UUID). Outbound DDI: `number` (E.164 PK).
    // Outbound from registration must use `id` — `number` is only for PhoneNumber rows.
    const listenPayload =
      (mediaType === 'inbound-call' && endpointId) || (mediaType === 'outbound-call' && endpointId)
        ? { id: endpointId }
        : {
            number:
              mediaType === 'inbound-call' || mediaType === 'outbound-call'
                ? (phoneNumber || '*')
                : undefined,
          };
    ({ data: instance } = await api.post(`/agents/${agentId}/listen`, {
      ...listenPayload,
      // Concurrency cap for the listener. 2 (not 1) so an in-set transfer isn't
      // blocked: transfer_agent hands the live call from one voice agent to
      // another, and the outgoing + incoming agents overlap briefly, so a cap of
      // 1 would reject the incoming agent. Still exercises server-side enforcement.
      options: { streamLog: true, recording: { enabled: true }, agentLimit: 2 },
    }));
    // Keep the listener id in module scope: `stopAgent()` relies on it for cleanup.
    // Avoid shadowing the module-level `listenerId` variable.
    const { socket, id } = instance || {};
    listenerId = id;
    let livekit, ultravox, pipecat;
    if (mediaType === 'webrtc' && listenerId) {
      ({ data: { livekit, ultravox, pipecat } } = await api.post(`/rooms/${listenerId}/join`, {}));
    } else if (mediaType === 'outbound-call' && listenerId) {
      const callerIdForOriginate = endpointId || phoneNumber;
      await api.post(`/listener/${listenerId}/originate`, {
        calledId: outboundNumber,
        callerId: callerIdForOriginate,
      });
    }
    if (socket) {
      const wsPath = `${backend.protocol === 'https:' ? 'wss:' : 'ws:'}//${backend.host}${socket}`;
      ws = new WebSocket(wsPath);
      ws.addEventListener('message', async (message) => {
        try {
          const data = JSON.parse(message.data);
          onMessage && onMessage(data);
        } catch (e) {}
      });
      ws.addEventListener('error', (err) => console.error(err, 'ws error'));
      ws.addEventListener('close', (err) => onClose && onClose(err?.message));
    }
    if (ultravox) {
      const debugMessages = new Set(['debug']);
      let calls = [];
      let transcriptLen = 0;
      session = new UltravoxSession({ experimentalMessages: debugMessages });
      (functions || []).filter((f) => f.implementation === 'client').forEach((func) => {
        session.registerToolImplementation(func.name, () => func.result);
      });
      ['status', 'transcripts', 'experimental_message'].forEach((event) =>
        session.addEventListener(event, (msg) => {
          const { transcripts } = session || {};
          let call;
          const debug = msg?.message?.message;
          if (debug && debug.startsWith('LLM response: \nTool calls:')) {
            const [[, method, body]] = debug.matchAll(/FunctionCall\(name='([^']*)'.*args='([^']*)'.*\)/gi).toArray() || [[]];
            call = { rest_callout: { method, body, url: '' } };
          } else if (debug && debug.startsWith('Tool call complete. Result: ')) {
            const [[, body, name]] = debug.matchAll(/.*role: MESSAGE_ROLE_TOOL_RESULT[\s\S]*text: "(.*)"[\s\S]*tool_name: "(.*)"/gi).toArray() || [[]];
            call = { function_results: [{ name, input: [], result: body?.replace(/\\/g, '') }] };
          }
          call && (calls[transcriptLen] = (calls[transcriptLen] || [])) && calls[transcriptLen].push(call);
          transcripts && onMessage && onMessage(transcripts.map((t, ind) => ([{ [t.speaker]: t.text }, ...(calls[ind] || [])])).flat());
          transcriptLen = (transcripts?.length - 1) ?? transcriptLen;
        })
      );
      session.joinCall(ultravox.joinUrl);
      ws = true;
    } else if (livekit) {
      const { serverUrl, participantToken } = livekit;
      const audioElement = new Audio();
      room = new Room({ disconnectOnPageLeave: true });
      room
        .on(RoomEvent.TrackSubscribed, (track) => track.attach(audioElement))
        .on(RoomEvent.TrackUnsubscribed, (track) => track.detach())
        .on(RoomEvent.Disconnected, () => {});
      await room.connect(serverUrl, participantToken);
      room.localParticipant.setMicrophoneEnabled(true);
      audioElement.play();
      ws = true;
    } else if (pipecat) {
      // Pipecat SmallWebRTCTransport (peer-to-peer WebRTC to a worker).
      //
      // The server-side handler (lib/handlers/pipecat.js) returns an offer
      // URL with the signed join token already baked in as a `?token=` query
      // param, so we can drive the stock @pipecat-ai/small-webrtc-transport
      // client without overriding its request body.
      //
      // Transcripts flow from the worker → llm-agent transaction-log →
      // the listener's WebSocket socket → `ws.onmessage` above (same path
      // as the LiveKit branch uses). We do NOT subscribe to PipecatClient's
      // onBotTranscript / onUserTranscript here — those would emit duplicate
      // entries.
      const { offerUrl } = pipecat;

      // Mount a hidden audio element so the bot's audio track has somewhere
      // to play. The @pipecat-ai/client-js docs note that for plain JS you
      // need to attach incoming audio yourself; PipecatClientAudio in React
      // does this implicitly but there's no equivalent for vanilla JS.
      const audioElement = new Audio();
      audioElement.autoplay = true;
      // Hidden but in the DOM — autoplay needs an element attached to the
      // document for some browsers (notably Chromium-based mobile).
      audioElement.style.display = 'none';
      document.body.appendChild(audioElement);

      // Dedupe set so we never attach the same track id twice. Some
      // transports re-fire onTrackStarted on reconnects.
      const attachedTrackIds = new Set();

      pipecatClient = new PipecatClient({
        transport: new SmallWebRTCTransport({
          iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
        }),
        enableMic: true,
        enableCam: false,
        callbacks: {
          onConnected: () => console.log('pipecat connected'),
          onDisconnected: () => {
            try {
              audioElement.pause();
              audioElement.srcObject = null;
              audioElement.remove();
            } catch (e) { /* ignore */ }
            onClose && onClose('pipecat disconnected');
          },
          // The bot's audio track lands here. Filtering rule:
          //
          //   - If `participant.local === true` we know it's the local mic
          //     and skip — attaching it to an <audio> element makes the
          //     user hear themselves.
          //   - If `participant` is undefined we attach. SmallWebRTCTransport
          //     fires onTrackStarted for the bot's remote track WITHOUT a
          //     participant object (there's only one remote so it's
          //     implicit), so an over-broad guard that also rejected
          //     undefined-participant tracks would silence the bot — which
          //     was the previous regression.
          //   - Anything else (participant exists, local !== true) is by
          //     definition a remote (bot) track and we attach.
          //
          // Track id dedupe defends against the SDK re-firing the same
          // track on reconnect, which would re-attach and could cause an
          // audible glitch.
          onTrackStarted: (track, participant) => {
            try {
              if (track?.kind !== 'audio') return;
              if (participant?.local === true) return;
              if (track.id && attachedTrackIds.has(track.id)) return;
              if (track.id) attachedTrackIds.add(track.id);
              const stream = new MediaStream([track]);
              audioElement.srcObject = stream;
              audioElement.play().catch((err) => {
                // Autoplay-blocked errors surface here; in the playground
                // the user already clicked "Talk to Agent" so the gesture
                // requirement is satisfied, but log for diagnosis.
                console.warn('audio.play() rejected', err);
              });
            } catch (err) {
              console.error('onTrackStarted handler failed', err);
            }
          },
        },
      });
      await pipecatClient.connect({ webrtcUrl: offerUrl });
      ws = true;
    }
    return { ws, id: agentId, listenerId: instance?.id };
  } catch (e) {
    console.error(e, 'startAgentSession error');
    throw new Error("Can't start agent session");
  }
}

export async function createAgent({ agentId, name, description, modelName, prompt, options, functions, mcpServers, keys, onClose, onMessage, audioModel, mediaType, phoneNumber, outboundNumber, endpointId }) {
  console.log({ agentId, modelName, prompt, options, onClose, onMessage, audioModel, mediaType, phoneNumber, outboundNumber, endpointId }, 'createAgent');
  let agent;
  let id = agentId;
  if (process.env.REACT_APP_STAGE === 'dev' && !id) {
    try {
      const dataFile = '../testdata.js';
      const testdata = (await import(dataFile)).default;
      ({ data: agent } = await api.post('/agents', testdata));
      id = agent?.id;
    } catch (e) {
      console.error(e, 'testdata error');
    }
  }

  try {
    if (!id) {
      if (!agent) {
        ({ data: agent } = await api.post('/agents', {
          name,
          description,
          modelName,
          prompt,
          options,
          functions,
          mcpServers,
          keys,
        }));
      }
      id = agent?.id;
    } else {
      // Update existing agent definition before starting a new session
      await updateAgent({
        id,
        name,
        description,
        modelName,
        prompt,
        options,
        functions,
        mcpServers,
        keys,
      });
    }
    const result = await startAgentSession(id, {
      options,
      functions,
      onClose,
      onMessage,
      audioModel,
      mediaType,
      phoneNumber,
      outboundNumber,
      endpointId,
    });
    return { ...result, id };
  } catch (e) {
    console.error(e, 'createAgent error');
    if (!agentId && agent?.id) {
      try {
        await stopAgent({ id: agent.id });
        await deleteAgent({ id: agent.id });
      } catch (cleanupErr) {
        console.error(cleanupErr, 'createAgent cleanup');
      }
    }
    throw new Error("Can't create agent");
  }
}

export async function listModels() {
  let { data } = await api.get('/models');
  console.log({ data }, 'models');
  return Object.entries(data);
}

/** True for built-in platform agents (e.g. `builtin:set-builder`), which are addressed by a `builtin:` id. */
export function isBuiltinAgentId(id) {
  return typeof id === 'string' && id.startsWith('builtin:');
}

/**
 * List agent summaries for the current user (paginated).
 * Same response shape as GET /calls: `{ agents, next }` where `next` is the next offset or `false`.
 *
 * Built-in platform agents (e.g. the set-builder) are filtered out here so they
 * never surface in any agent list/picker. They stay reachable through their own
 * flows by well-known id (e.g. the set-builder chat), just not as selectable
 * agents. This client-side filter obfuscates them immediately, ahead of the
 * backend suppressing them at source in a later release.
 * @param {{ offset?: number, limit?: number, search?: string, searchField?: 'all'|'name'|'model' }} [opts]
 */
export async function listAgents(opts = {}) {
  const params = new URLSearchParams();
  params.set('limit', String(opts.limit ?? 50));
  params.set('offset', String(opts.offset ?? 0));
  const q = opts.search != null ? String(opts.search).trim() : '';
  if (q) params.set('search', q);
  if (opts.searchField && opts.searchField !== 'all') params.set('searchField', opts.searchField);
  const { data } = await api.get(`/agents?${params.toString()}`);
  if (data && Array.isArray(data.agents)) {
    data.agents = data.agents.filter((a) => a?.builtin !== true && !isBuiltinAgentId(a?.id));
  }
  return data;
}

/** Fetch a single agent definition by id (for Load dialog and Observe/Overview). */
export async function getAgent(id) {
  const { data } = await api.get(`/agents/${id}`);
  return data;
}

/**
 * Active listener instances (deployments) for the current user.
 * @param {{ agentId?: string }} [opts] If `agentId` is set, only listeners for that agent (must be in scope).
 */
export async function listDeployments(opts = {}) {
  const params = new URLSearchParams();
  if (opts.agentId) params.set('agentId', opts.agentId);
  const q = params.toString();
  const { data } = await api.get(q ? `/listener?${q}` : '/listener');
  return data;
}

// agentLimit 2 (not 1) leaves headroom for an in-set transfer: transfer_agent
// briefly overlaps the outgoing + incoming voice agents, which a cap of 1 blocks.
const deploymentListenOptions = { streamLog: true, recording: { enabled: true }, agentLimit: 2 };

/**
 * Start a long-lived listener (deployment) for an agent — WebRTC room, phone number, or SIP registration endpoint.
 * @param {'webrtc'|'phone'|'registration'} kind
 * @param {string} [phoneNumberOrEndpointId]
 *   - For `phone`: E.164 for an allocated number, or "*" for ephemeral.
 *   - For `registration`: phone endpoint id for a `phone-registration` endpoint.
 */
export async function createDeployment(agentId, kind, phoneNumberOrEndpointId) {
  if (kind === 'webrtc') {
    const { data } = await api.post(`/agents/${agentId}/listen`, { options: { ...deploymentListenOptions } });
    return data;
  }

  if (kind === 'registration') {
    if (!phoneNumberOrEndpointId) {
      throw new Error('Endpoint id is required for registration deployments');
    }
    const { data } = await api.post(`/agents/${agentId}/listen`, {
      id: phoneNumberOrEndpointId,
      options: { ...deploymentListenOptions },
    });
    return data;
  }

  const number = phoneNumberOrEndpointId ?? '*';
  const { data } = await api.post(`/agents/${agentId}/listen`, {
    number,
    options: { ...deploymentListenOptions },
  });
  return data;
}

export async function deleteDeploymentListener(listenerId) {
  await api.delete(`/listener/${listenerId}`);
}

/** WebRTC listener id + room key for @aplisay/react-widget (see https://widget.aplisay.com). */
export async function getListenerRoomKey(listenerId) {
  const { data } = await api.get(`/listener/${listenerId}/room-key`);
  return data;
}

/**
 * Open the transcript/log stream for a listener (deployment) started with
 * `streamLog`. `socketPath` is the `socket` field from the createDeployment /
 * listen response. Each incoming frame is JSON-parsed and handed to `onMessage`
 * (an array = the full transcript so far, an object = one incremental
 * utterance/event); `onClose` fires when the socket closes. Returns the WebSocket
 * so the caller can close it on teardown.
 */
export function openListenerSocket(socketPath, { onMessage, onClose } = {}) {
  const url = `${backend.protocol === 'https:' ? 'wss:' : 'ws:'}//${backend.host}${socketPath}`;
  const sock = new WebSocket(url);
  sock.addEventListener('message', (message) => {
    try { onMessage?.(JSON.parse(message.data)); } catch (e) { /* ignore non-JSON frames */ }
  });
  sock.addEventListener('error', (err) => console.error(err, 'listener socket error'));
  sock.addEventListener('close', (e) => onClose?.(e?.reason));
  return sock;
}

export async function updateAgent({ id, name, description, modelName, prompt, options, functions, mcpServers, keys }) {
  const body = { prompt, options, functions, mcpServers };
  if (name != null) body.name = name;
  if (description != null) body.description = description;
  if (modelName != null) body.modelName = modelName;
  if (keys != null) body.keys = keys;
  const { data } = await api.put(`/agents/${id}`, body);
  return data;
}

export async function stopAgent({ id }) {
  try {
    console.log({ id, session, listenerId, socket }, 'stopAgent');
    session && session.leaveCall && session.leaveCall();
    room && room.disconnect && room.disconnect();
    pipecatClient && pipecatClient.disconnect && pipecatClient.disconnect();
    room = session = pipecatClient = null;
    ws && ws.close && ws.close();

    listenerId && await api.delete(`/agents/${id}/listen/${listenerId}`);
    listenerId = undefined;
  }
  catch (e) {
    // we may be trying to delete an agent because of a failed network, don't care too much
  }
}

export async function deleteAgent({ id }) {
  try {
    console.log({ id, session }, 'deleteAgent');
    session && session.leaveCall && session.leaveCall();
    room && room.disconnect && room.disconnect();
    pipecatClient && pipecatClient.disconnect && pipecatClient.disconnect();
    room = session = pipecatClient = null;
    ws && ws.close && ws.close();
    let { data } = id && await api.delete(`/agents/${id}`);
    return data;
  }
  catch (e) {
    // we may be trying to delete an agent because of a failed network, don't care too much
  }
}

/** @deprecated Prefer {@link listModelVoiceLocales} + {@link listModelVoicesForLocale}. */
export async function listVoices() {
  let { data } = await api.get('/voices');
  return data;
}

/** Locales for the language selector for this model (URL-encodes model id). */
export async function listModelVoiceLocales(modelName) {
  const { data } = await api.get(
    `/models/${encodeURIComponent(modelName)}/voices`,
  );
  return data;
}

/** Vendor → voice rows for one locale (URL-encodes model id and locale). */
export async function listModelVoicesForLocale(modelName, locale) {
  const { data } = await api.get(
    `/models/${encodeURIComponent(modelName)}/voices/${encodeURIComponent(locale)}`,
  );
  return data;
}

/** STT provider options for one locale (URL-encodes model id and locale). */
export async function listModelRecognitionForLocale(modelName, locale) {
  const { data } = await api.get(
    `/models/${encodeURIComponent(modelName)}/recognition/${encodeURIComponent(locale)}`,
  );
  return data;
}

export async function listPhoneNumbers(originate = false, handler = null) {
  let params = new URLSearchParams();
  if (originate) params.append('originate', 'true');
  if (handler) params.append('handler', handler);

  let { data } = await api.get(`/phone-numbers${params.toString() ? '?' + params.toString() : ''}`);
  return data;
}

/** List calls for an agent with optional date range and pagination. */
export async function listCallsForAgent(
  agentId,
  { startDate, endDate, offset = 0, limit = 50 } = {}
) {
  const params = new URLSearchParams();
  if (startDate != null) params.append('startDate', new Date(startDate).toISOString());
  if (endDate != null) params.append('endDate', new Date(endDate).toISOString());
  params.append('offset', String(offset));
  params.append('limit', String(limit));
  const { data } = await api.get(`/agents/${agentId}/calls?${params.toString()}`);
  return data;
}

/**
 * List calls across the whole organisation (paginated by `index`).
 * API: GET /calls?limit=<n>[&startDate][&endDate][&lastIndex][&order=asc|desc]
 * Response: { calls, next } where `next` is the last index in the returned page or `false`.
 */
export async function listAllCalls({ startDate, endDate, lastIndex = 0, limit = 50 } = {}) {
  const params = new URLSearchParams();
  if (startDate != null) params.append('startDate', new Date(startDate).toISOString());
  if (endDate != null) params.append('endDate', new Date(endDate).toISOString());
  params.append('lastIndex', String(lastIndex ?? 0));
  params.append('limit', String(limit ?? 50));
  const { data } = await api.get(`/calls?${params.toString()}`);
  return data;
}

/**
 * List the whole call tree (transfer chain) a call belongs to.
 * API: GET /calls/{callId}/linked — given any leg, resolves the root via `parentId`
 * and returns the root plus all descendant legs, each carrying its own
 * agentId/agentName/agentLabel/parentId. Linked by parentId (durable), so a
 * transferred-to agent's leg is included and it works for historical calls.
 */
export async function getLinkedCalls(callId, { limit = 25 } = {}) {
  const { data } = await api.get(`/calls/${callId}/linked?limit=${limit}`);
  return data;
}

/** Get invocation log records for a call. */
export async function getCallInvocationLog(callId) {
  const { data } = await api.get(`/calls/${callId}/invocation-log`);
  return data;
}

/** Get transaction log records for a call (transcript source for Observe). */
export async function getCallTransactionLog(callId) {
  const { data } = await api.get(`/calls/${callId}/logs`);
  return data;
}

/** Fetch call recording as a blob and return an object URL for use in <audio src>. Caller must revoke the URL when done. */
export async function getCallRecordingBlob(callId) {
  const { data } = await api.get(`/calls/${callId}/recording`, { responseType: 'blob' });
  return URL.createObjectURL(data);
}